import { useState, useCallback, useRef } from "react";
import type { Source } from "../app";

interface StreamCallbacks {
  onSources: (sources: Source[]) => void;
  onChunk: (chunk: string) => void;
  onDone: () => void;
  onError: (error: string) => void;
}

/**
 * Adaptive drip controller that adjusts display speed based on token arrival rate.
 * - Tracks arrival rate using exponential moving average
 * - Catches up quickly if buffer grows too large
 * - Maintains smooth appearance during slow periods
 */
class AdaptiveDrip {
  private buffer = "";
  private isRunning = false;
  private lastArrivalTime = 0;
  private arrivalRate = 300; // chars/sec, initial estimate
  private readonly minRate = 50; // Never slower than 50 chars/sec
  private readonly maxRate = 2000; // Never faster than 2000 chars/sec
  private readonly catchUpThreshold = 100; // Buffer chars before catch-up mode

  constructor(private callback: (text: string) => void) {}

  push(text: string) {
    const now = performance.now();
    if (this.lastArrivalTime > 0) {
      const elapsed = (now - this.lastArrivalTime) / 1000;
      if (elapsed > 0) {
        // Exponential moving average of arrival rate
        const instantRate = text.length / elapsed;
        this.arrivalRate = 0.3 * instantRate + 0.7 * this.arrivalRate;
        this.arrivalRate = Math.max(this.minRate, Math.min(this.maxRate, this.arrivalRate));
      }
    }
    this.lastArrivalTime = now;
    this.buffer += text;

    if (!this.isRunning) {
      this.run();
    }
  }

  private async run() {
    this.isRunning = true;

    while (this.buffer.length > 0) {
      // Catch-up mode: emit larger chunks if buffer is growing
      const isCatchingUp = this.buffer.length > this.catchUpThreshold;
      const charsPerBatch = isCatchingUp
        ? Math.min(20, Math.ceil(this.buffer.length / 10))
        : Math.max(1, Math.ceil(this.arrivalRate / 100));

      const batch = this.buffer.slice(0, charsPerBatch);
      this.buffer = this.buffer.slice(charsPerBatch);
      this.callback(batch);

      // Adaptive delay based on arrival rate
      const delayMs = isCatchingUp ? 5 : Math.max(5, (1000 / this.arrivalRate) * charsPerBatch);
      await new Promise((r) => setTimeout(r, delayMs));
    }

    this.isRunning = false;
  }

  flush() {
    if (this.buffer.length > 0) {
      this.callback(this.buffer);
      this.buffer = "";
    }
  }
}

export function useStreamingAnswer() {
  const [isStreaming, setIsStreaming] = useState(false);
  const abortRef = useRef<AbortController | null>(null);

  const streamAnswer = useCallback(async (question: string, callbacks: StreamCallbacks) => {
    // Cancel any previous stream
    if (abortRef.current) {
      abortRef.current.abort();
    }
    abortRef.current = new AbortController();

    setIsStreaming(true);

    // Create adaptive dripper for this stream
    const dripper = new AdaptiveDrip(callbacks.onChunk);

    try {
      const response = await fetch("/api/ask", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question }),
        signal: abortRef.current.signal,
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to fetch answer");
      }

      const reader = response.body?.getReader();
      if (!reader) throw new Error("No reader available");

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Parse newline-delimited JSON events
        const lines = buffer.split("\n");
        buffer = lines.pop() || ""; // Keep incomplete line in buffer

        for (const line of lines) {
          if (!line.trim()) continue;

          try {
            const event = JSON.parse(line);

            switch (event.type) {
              case "sources":
                callbacks.onSources(event.data);
                break;
              case "chunk":
                // Push to adaptive dripper (non-blocking)
                dripper.push(event.data);
                break;
              case "done":
                // Flush remaining buffer before signaling done
                dripper.flush();
                callbacks.onDone();
                break;
              case "error":
                callbacks.onError(event.data);
                break;
            }
          } catch {
            // Skip malformed JSON
            console.warn("Failed to parse event:", line);
          }
        }
      }

      // Process any remaining buffer
      if (buffer.trim()) {
        try {
          const event = JSON.parse(buffer);
          if (event.type === "done") {
            dripper.flush();
            callbacks.onDone();
          }
        } catch {
          // Ignore
        }
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") {
        // Stream was cancelled, ignore
        return;
      }
      console.error("Streaming error:", error);
      callbacks.onError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsStreaming(false);
      callbacks.onDone();
    }
  }, []);

  return { isStreaming, streamAnswer };
}
